import { useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { TopBar } from './TopBar';
import { BottomNav } from './BottomNav';
import { isNative, isAndroid } from '@/lib/capacitor';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { supabase } from '@/lib/supabase';
import { useDeletedPregnancyCheck } from '@/hooks/useDeletedPregnancyCheck';
import { PregnancyDeletedModal } from '@/components/PregnancyDeletedModal';
import { Disclaimer } from '@/components/Disclaimer';
import { usePushNotifications } from '@/features/notifications';
import { useSwipeBack } from '@/hooks/useSwipeBack';

// Routes, die keinen Disclaimer bekommen sollen.
// Bewusst als eigenes Array in diesem Modul gehalten (gleiches Muster wie
// MAIN_ROUTES in TopBar.tsx), nicht mit den dortigen Listen zusammengelegt -
// die Kriterien sind andere und sollen sich unabhängig entwickeln können.
//
// Exakte Pfade, wie bei MAIN_ROUTES - via Array.includes(), kein
// startsWith(). /profile und /tools stehen hier nur als Landingpages selbst;
// ihre Unterseiten (z.B. /tools/scanner, /tools/contractions,
// /tools/guides, /tools/sleep-plan, /profile/notifications) matchen NICHT
// und behalten den Disclaimer.
const DISCLAIMER_EXCLUDED_ROUTES: string[] = ['/profile', '/tools'];

interface AppLayoutProps {
  children?: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user } = useAuth();
  const location = useLocation();
  const showDisclaimer = !DISCLAIMER_EXCLUDED_ROUTES.includes(location.pathname);
  const { currentPregnancy, setPregnancy, setLoading, setLastCompletedPregnancy, lastCompletedPregnancy } = usePregnancyStore();
  const { showModal: showDeletedModal, closeModal: closeDeletedModal } = useDeletedPregnancyCheck();

  // Initialize push notifications for native apps
  // The hook handles permission requests and token registration automatically
  usePushNotifications();

  // Enable edge-swipe-back gesture on native platforms (iOS/Android)
  useSwipeBack();

  // Handle pull-to-refresh - reloads the current page
  const handleRefresh = useCallback(async () => {
    // Force a page reload by triggering React Router navigation
    // This will re-run all useEffect hooks and refetch data
    window.location.reload();
  }, []);

  // Fetch pregnancy data globally for all routes using this layout
  useEffect(() => {
    async function fetchPregnancy() {
      // Skip if no user or pregnancy already loaded
      if (!user) return;
      if (currentPregnancy) return;

      setLoading(true);
      try {
        // First, try to get active pregnancy created by user
        let pregnancy = null;

        const { data: ownPregnancies, error: ownError } = await supabase
          .from('pregnancies')
          .select('*')
          .eq('created_by', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1);

        if (ownError) {
          console.error('Error fetching own pregnancy:', ownError);
          setLoading(false);
          return;
        }

        pregnancy = ownPregnancies?.[0] || null;

        // If no own active pregnancy, check if user is a partner in an active connection
        if (!pregnancy) {
          console.log('[AppLayout] Checking for partner connection, user_id:', user.id);

          const { data: partnerConnection, error: partnerError } = await supabase
            .from('partner_connections')
            .select('pregnancy_id')
            .eq('user_2_id', user.id)
            .eq('status', 'active')
            .maybeSingle();

          if (partnerError) {
            console.error('[AppLayout] Error fetching partner connection:', partnerError);
          } else {
            console.log('[AppLayout] Partner connection result:', partnerConnection);
          }

          if (partnerConnection?.pregnancy_id) {
            const { data: sharedPregnancy, error: sharedError } = await supabase
              .from('pregnancies')
              .select('*')
              .eq('id', partnerConnection.pregnancy_id)
              .eq('is_active', true)
              .maybeSingle();

            if (sharedError) {
              console.error('[AppLayout] Error fetching shared pregnancy:', sharedError);
            } else {
              console.log('[AppLayout] Shared pregnancy result:', sharedPregnancy);
            }

            pregnancy = sharedPregnancy || null;
          }
        }

        setPregnancy(pregnancy);
      } catch (err) {
        console.error('Error loading pregnancy data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchPregnancy();
  }, [user, currentPregnancy, setPregnancy, setLoading]);

  // Fetch last completed pregnancy for baby mode (if no active pregnancy)
  useEffect(() => {
    async function fetchCompletedPregnancy() {
      if (!user) return;
      if (currentPregnancy) return; // Don't need completed if active exists
      if (lastCompletedPregnancy) return; // Already loaded

      try {
        // Get the most recent completed pregnancy (own or via family connection)
        const { data: completedPregnancy, error } = await supabase
          .from('pregnancies')
          .select('*')
          .eq('created_by', user.id)
          .eq('is_active', false)
          .not('completion_type', 'is', null)
          .order('completion_date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('[AppLayout] Error fetching completed pregnancy:', error);
          return;
        }

        if (completedPregnancy) {
          console.log('[AppLayout] Found completed pregnancy:', completedPregnancy.id);
          setLastCompletedPregnancy(completedPregnancy);
        } else {
          // Check for partner's completed pregnancy via family connection
          const { data: familyConnection } = await supabase
            .from('partner_connections')
            .select('pregnancy_id')
            .or(`user_1_id.eq.${user.id},user_2_id.eq.${user.id}`)
            .eq('is_family_connection', true)
            .maybeSingle();

          if (familyConnection?.pregnancy_id) {
            const { data: partnerPregnancy } = await supabase
              .from('pregnancies')
              .select('*')
              .eq('id', familyConnection.pregnancy_id)
              .maybeSingle();

            if (partnerPregnancy) {
              console.log('[AppLayout] Found partner completed pregnancy:', partnerPregnancy.id);
              setLastCompletedPregnancy(partnerPregnancy);
            }
          }
        }
      } catch (err) {
        console.error('[AppLayout] Error loading completed pregnancy:', err);
      }
    }

    fetchCompletedPregnancy();
  }, [user, currentPregnancy, lastCompletedPregnancy, setLastCompletedPregnancy]);

  // Custom refresh indicator component
  const RefreshIndicator = (
    <div className="flex items-center justify-center py-4">
      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
    </div>
  );

  // Native App: Padding for fixed header/footer containers with Pull-to-Refresh
  if (isNative) {
    return (
      <>
        <div className="min-h-screen flex flex-col">
          <TopBar />
          <main
            className="flex-1 overflow-y-auto native-scroll-container"
            style={{
              // TopBar container height: safe-area + ~56px (py-3 + content)
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 56px)',
              // BottomNav container height:
              // - Android: fixed ~52px (no safe-area, nav sits directly above system buttons)
              // - iOS: safe-area + ~56px (includes home indicator area)
              paddingBottom: isAndroid ? '52px' : 'calc(env(safe-area-inset-bottom, 0px) + 56px)',
            }}
          >
            <PullToRefresh
              onRefresh={handleRefresh}
              pullingContent={RefreshIndicator}
              refreshingContent={RefreshIndicator}
              resistance={2}
              maxPullDownDistance={100}
              pullDownThreshold={67}
            >
              <div className="max-w-[767px] mx-auto px-4 py-4 min-h-full">
                {/* .page-outlet ist `display: contents` - reiner Marker, damit
                    der Disclaimer bei leerem Outlet ausgeblendet wird. */}
                <div className="page-outlet">{children || <Outlet />}</div>
                {showDisclaimer && <Disclaimer />}
              </div>
            </PullToRefresh>
          </main>
          <BottomNav />
        </div>
        <PregnancyDeletedModal
          isOpen={showDeletedModal}
          onClose={closeDeletedModal}
        />
      </>
    );
  }

  // Web App: Original padding for floating nav elements
  return (
    <>
      <div className="min-h-screen flex flex-col">
        <TopBar />
        <main className="flex-1 overflow-y-auto py-20">
          <div className="max-w-[767px] mx-auto px-4">
            {/* .page-outlet ist `display: contents` - reiner Marker, damit
                der Disclaimer bei leerem Outlet ausgeblendet wird. */}
            <div className="page-outlet">{children || <Outlet />}</div>
            {showDisclaimer && <Disclaimer />}
          </div>
        </main>
        <BottomNav />
      </div>
      <PregnancyDeletedModal
        isOpen={showDeletedModal}
        onClose={closeDeletedModal}
      />
    </>
  );
}
